// DOM HUD: energy/damage bars, rotor + sink gauges, coins, mission line with
// compass arrow and deadline bar, toasts, big centre messages, TAB scoreboard.
// Rule: NOTHING in here creates a DOM node per frame.
export function buildHud() {
  const root = document.createElement('div');
  root.id = 'hud';
  root.innerHTML = `
    <div id="topbar">
      <div class="gaugewrap">
        <div class="gauge" id="rotorg" title="Rotor — hold pilen i det grønne bånd">
          <div class="gband" id="hovband"></div>
          <div class="gfill" id="rotorfill"></div>
          <div class="gmark" id="rotormark"></div>
        </div>
        <div class="gauge" id="sinkg" title="Synkefart — grøn = blid landing">
          <div class="gfill" id="sinkfill"></div>
        </div>
        <div class="glbls"><span>ROTOR</span><span>SYNK</span></div>
      </div>
      <div class="bars">
        <div class="barwrap"><span class="lbl">ENERGI</span><div class="bar"><div id="energyfill"></div></div></div>
        <div class="barwrap"><span class="lbl">SKADE</span><div class="bar"><div id="damagefill"></div></div></div>
      </div>
      <div class="pickups"><span id="stone" class="off">🪨</span><button id="snd" title="Lyd til/fra (M)">🔊</button></div>
      <div class="rightcol">
        <div id="coins">🪙 0</div>
        <div id="streak" class="off">🔥 0 i træk</div>
      </div>
    </div>
    <div id="mission">
      <span id="levelchip">BANE 1</span><span id="compass">➤</span><span id="missiontext">Vent på en kunde…</span>
      <div id="missionbar"><div id="missionbarfill"></div></div>
    </div>
    <div id="telemetry"></div>
    <div id="toasts"></div>
    <div id="bigmsg"></div>
    <div id="flash"></div>
    <div id="board" style="display:none"></div>
  `;
  document.body.appendChild(root);

  const el = id => root.querySelector('#' + id);
  const stoneEl = el('stone');
  const energyfill = el('energyfill'), damagefill = el('damagefill');
  const coinsEl = el('coins'), missiontext = el('missiontext'), compass = el('compass');
  const toasts = el('toasts'), bigmsg = el('bigmsg'), telem = el('telemetry'), board = el('board');
  const flashEl = el('flash');
  const levelchip = el('levelchip');
  const rotorfill = el('rotorfill'), hovband = el('hovband'), rotormark = el('rotormark');
  const sinkfill = el('sinkfill');
  const streakEl = el('streak'), sndEl = el('snd');
  const missionbar = el('missionbar'), missionbarfill = el('missionbarfill');

  let coinShown = 0, stoneShown = -1, streakShown = -1, missionShown = '', boardShown = '';
  // The compass is accumulated, never assigned raw: -(dir - camYaw) jumps by 2π
  // every time the target passes behind you, and a .12s CSS transition then
  // whips the arrow the long way round — exactly while circling a pad.
  let compassAcc = 0, compassRaw = null;
  const wrapPi = a => Math.atan2(Math.sin(a), Math.cos(a));

  // ---- toasts: at most 3 on screen, deduped, danger outranks the rest
  const PRIO = { fare: 3, good: 2, info: 1 };
  const live = [];   // {node, msg, prio, timer}

  function dropToast(entry) {
    const i = live.indexOf(entry);
    if (i >= 0) live.splice(i, 1);
    clearTimeout(entry.timer);
    entry.node.classList.remove('show');
    setTimeout(() => entry.node.remove(), 400);
  }

  return {
    update({ energy, damage, coins, speed, alt, rotor = 0, hoverBand, sink = 0, landQ = 1 }) {
      energyfill.style.width = Math.max(0, energy).toFixed(0) + '%';
      energyfill.style.background = energy > 35 ? '#7ec850' : energy > 15 ? '#e8b840' : '#e05038';
      damagefill.style.width = Math.max(0, damage).toFixed(0) + '%';
      if (coins !== coinShown) {
        coinShown = coins;
        coinsEl.textContent = '🪙 ' + coins;
        coinsEl.classList.remove('pop'); void coinsEl.offsetWidth; coinsEl.classList.add('pop');
      }
      // rotor gauge — it really can read 0 in the air now, so no floor here
      const r = Math.max(0, Math.min(1, rotor));
      rotorfill.style.height = (r * 100).toFixed(0) + '%';
      rotorfill.style.background = r < 0.30 ? '#e05038' : '#cfe4c0';
      rotormark.style.bottom = (r * 100).toFixed(0) + '%';
      rotormark.style.opacity = r < 0.30 ? '1' : '0';
      if (hoverBand) {
        const lo = Math.max(0, Math.min(1, hoverBand[0])), hi = Math.max(0, Math.min(1, hoverBand[1]));
        hovband.style.bottom = (lo * 100).toFixed(1) + '%';
        hovband.style.height = Math.max(2, (hi - lo) * 100).toFixed(1) + '%';
      }
      // sink gauge — coloured by the landing verdict you would get right now
      const sk = Math.max(0, Math.min(1, sink / 9));
      sinkfill.style.height = (sk * 100).toFixed(0) + '%';
      sinkfill.style.background = landQ > 0.85 ? '#7ec850' : landQ > 0.45 ? '#e8b840' : '#e05038';
      telem.textContent = `${speed.toFixed(0)} m/s · ${alt.toFixed(0)} m`;
    },
    // frac: 0..1 of the customer's remaining patience, or null for no deadline
    setMission(text, frac = null) {
      if (text !== missionShown) { missionShown = text; missiontext.innerHTML = text; }
      if (frac === null) {
        if (missionbar.style.display !== 'none') missionbar.style.display = 'none';
      } else {
        if (missionbar.style.display === 'none') missionbar.style.display = '';
        missionbarfill.style.width = (frac * 100).toFixed(1) + '%';
        missionbarfill.style.background = frac > 0.5 ? '#7ec850' : frac > 0.22 ? '#e8b840' : '#e05038';
      }
    },
    setLevel({ n, delivered, quota }) {
      levelchip.innerHTML = `BANE ${n} <small>· ${delivered}/${quota}</small>`;
    },
    // n = 0-3 stones in the net
    setStone(n) {
      const c = Math.max(0, n | 0);
      if (c === stoneShown) return;
      stoneShown = c;
      stoneEl.classList.toggle('off', c === 0);
      stoneEl.textContent = c > 1 ? `🪨×${c}` : '🪨';
      stoneEl.title = c ? 'Tryk F for at kaste en sten' : 'Flyv gennem en stenbunke for at samle op';
    },
    setStreak(s, mult = 1) {
      if (s === streakShown) return;
      streakShown = s;
      streakEl.classList.toggle('off', s < 2);
      streakEl.textContent = `🔥 ${s} i træk · ×${Number(mult).toFixed(2)}`;
    },
    setSound(on) { sndEl.textContent = on ? '🔊' : '🔇'; },
    // angle: radians, screen-relative (0 = op/frem)
    setCompass(angle, visible) {
      compass.style.visibility = visible ? 'visible' : 'hidden';
      if (!visible) { compassRaw = null; return; }
      if (compassRaw === null) { compassAcc = angle; compassRaw = angle; }
      else { compassAcc += wrapPi(angle - compassRaw); compassRaw = angle; }
      compass.style.transform = `rotate(${compassAcc}rad)`;
    },
    // prio: 'fare' (danger) > 'good' > 'info'
    toast(msg, ms = 3500, prio = 'info') {
      const p = PRIO[prio] || 1;
      const dupe = live.find(e => e.msg === msg);
      if (dupe) {                                   // same warning again: just re-arm it
        clearTimeout(dupe.timer);
        dupe.timer = setTimeout(() => dropToast(dupe), ms);
        return;
      }
      if (live.length >= 3) {
        // evict the least important, oldest toast — never a louder one
        let victim = live[0];
        for (const e of live) if (e.prio < victim.prio) victim = e;
        if (victim.prio > p) return;                // everything on screen matters more
        dropToast(victim);
      }
      const d = document.createElement('div');
      d.className = 'toast' + (p === 3 ? ' fare' : p === 2 ? ' good' : '');
      d.innerHTML = msg;
      toasts.appendChild(d);
      const entry = { node: d, msg, prio: p, timer: 0 };
      live.push(entry);
      requestAnimationFrame(() => d.classList.add('show'));
      entry.timer = setTimeout(() => dropToast(entry), ms);
    },
    big(msg, ms = 2600) {
      bigmsg.innerHTML = msg; bigmsg.classList.add('show');
      clearTimeout(bigmsg._t);
      if (ms) bigmsg._t = setTimeout(() => bigmsg.classList.remove('show'), ms);
    },
    bigOff() { clearTimeout(bigmsg._t); bigmsg.classList.remove('show'); },
    flash(strength = 1) {
      flashEl.style.transition = 'none';
      flashEl.style.opacity = Math.min(1, strength).toFixed(2);
      requestAnimationFrame(() => {
        flashEl.style.transition = 'opacity .45s';
        flashEl.style.opacity = '0';
      });
    },
    // rebuilt ONLY when the standings actually change — TAB is often held down
    showBoard(rows, show) {
      board.style.display = show ? 'block' : 'none';
      if (!show) return;
      const html = '<h3>STAMMENS BEDSTE</h3>' + rows
        .map(r => `<div class="row${r.you ? ' you' : ''}"><span>${r.name}</span><span>🪙 ${r.coins}</span></div>`)
        .join('');
      if (html !== boardShown) { boardShown = html; board.innerHTML = html; }
    },
  };
}
